import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { content } from '../../../content/zci/content';

import { modifiedGranteeContent } from '../../../content/zci/grantee-content-modified';
import { modifiedGranteeAlmostThereContent } from '../../../content/zci/grantee-almost-there-content-modified';
import { modifiedGrantorContent } from '../../../content/zci/grantor-content-modified';

import { catchError, map, of } from 'rxjs';
import { LoggerService } from '../logger/logger.service';
import { transform } from '../../../aem/aem-utils';
import { modifiedGranteeWelcomeContent } from 'src/content/zci/grantee-welcome-content-modified';

@Injectable({
    providedIn: 'root',
})
export class ContentService {
    private http = inject(HttpClient);
    private logger = inject(LoggerService);


    /**
     * Fetches content based on the provided content type from local sources.
     * 
     * This method does not make any HTTP requests and serves the content 
     * exclusively from locally modified objects based on the content type.
     * 
     * @param contentType The type of content to fetch (e.g., "Grantee", "Grantor").
     * @returns The locally stored content for the specified type.
     */
    getContent() {
        return of(content);
    }
    /**
     * Fetches the "Grantee" content using a GraphQL query.
     * 
     * - In development mode, it serves the content from a locally modified object (`modifiedGranteeContent`).
     * - In production mode, it makes an HTTP GET request to fetch the content from the AEM GraphQL endpoint.
     * 
     * The method applies a transformation to the raw content and handles errors gracefully:
     * - Logs a warning message if the HTTP request fails.
     * - Falls back to serving the locally modified content in case of an error.
     * 
     * @returns An Observable containing the transformed "Grantee" content.
     */
    getGranteeGraphqlContent() {
        if (environment.development) {
            return of(modifiedGranteeContent);
        }
        return this.http
            .get<
                Record<any, any>
            >(`${environment.AEM_GRAPHQL.BASE_URL}${environment.AEM_GRAPHQL.CONF_NAME}${environment.AEM_GRAPHQL.GRANTEE_CONTENT_QUERY_NAME}`)
            .pipe(
                // TODO add runtime check for content?
                map((rawContent) => transform(rawContent, [])),
                catchError((error) => {
                    this.logger.warn(
                        `getGranteeGraphqlContent -  Serving content from local file as AEM call failed:
                        ${error?.message}`,
                    );
                    return of(modifiedGranteeContent);
                }),
            );
    }

    /**
     * Fetches the "Grantee Almost There" content using a GraphQL query.
     * 
     * - In development mode, it serves the content from a locally modified object (`modifiedGranteeAlmostThereContent`).
     * - In production mode, it makes an HTTP GET request to fetch the content from the AEM GraphQL endpoint.
     * 
     * The method applies a transformation to the raw content and handles errors gracefully:
     * - Logs an error message if the HTTP request fails.
     * - Falls back to serving the locally modified content in case of an error.
     * 
     * @returns An Observable containing the transformed "Grantee Almost There" content.
     */
    getGranteeAlmostThereGraphqlContent() {
        if (environment.development) {
            return of(modifiedGranteeAlmostThereContent);
        }
        return this.http
            .get<
                Record<any, any>
            >(`${environment.AEM_GRAPHQL.BASE_URL}${environment.AEM_GRAPHQL.CONF_NAME}${environment.AEM_GRAPHQL.GRANTEE_ALMOST_THERE_CONTENT_QUERY_NAME}`)
            .pipe(
                map((rawContent) => transform(rawContent, [])),
                catchError((error) => {
                    this.logger.warn(
                        `getGranteeAlmostThereGraphqlContent -  Serving content from local file as AEM call failed:
                        ${error?.message}`,
                    );
                    return of(modifiedGranteeAlmostThereContent);
                }),
            );
    }

    /**
     * Fetches the "Grantee Welcome" content using a GraphQL query.
     * 
     * - In development mode, it serves the content from a locally modified object (`modifiedGranteeWelcomeContent`).
     * - In production mode, it makes an HTTP GET request to fetch the content from the AEM GraphQL endpoint.
     * 
     * The method applies a transformation to the raw content and handles errors gracefully:
     * - Logs a warning message if the HTTP request fails.
     * - Falls back to serving the locally modified content in case of an error.
     * 
     * @returns An Observable containing the transformed "Grantee Welcome" content.
     */
    getGranteeWelcomeGraphqlContent() {
        if (environment.development) {
            return of(modifiedGranteeWelcomeContent);
        }
        return this.http
            .get<
                Record<any, any>
            >(`${environment.AEM_GRAPHQL.BASE_URL}${environment.AEM_GRAPHQL.CONF_NAME}${environment.AEM_GRAPHQL.GRANTEE_WELCOME_CONTENT_QUERY_NAME}`)
            .pipe(
                map((rawContent) => transform(rawContent, [])),
                catchError((error) => {
                    this.logger.warn(
                        `getGranteeWelcomeGraphqlContent - Serving content from local file as AEM call failed:
                        ${error?.message}`,
                    );
                    return of(modifiedGranteeWelcomeContent);
                }),
            );
    }

    /**
     * Fetches the Grantor content using a GraphQL query.
     * 
     * - In development mode, it serves the content from a locally modified object (`modifiedGrantorContent`).
     * - In production mode, it makes an HTTP GET request to fetch the content from the AEM GraphQL endpoint.
     * 
     * The method applies a transformation to the raw content and handles errors gracefully:
     * - Logs a warning message if the HTTP request fails.
     * - Falls back to serving the locally modified content in case of an error.
     * 
     * @returns An Observable containing the transformed Grantor content.
     */
    getGrantorGraphqlContent() {
        if (environment.development) {
            return of(modifiedGrantorContent);
        }
        return this.http
            .get<
                Record<any, any>
            >(`${environment.AEM_GRAPHQL.BASE_URL}${environment.AEM_GRAPHQL.CONF_NAME}${environment.AEM_GRAPHQL.GRANTOR_CONTENT_QUERY_NAME}`)
            .pipe(
                // TODO add runtime check for content?
                map((rawContent) => transform(rawContent, [])),
                catchError((error) => {
                    this.logger.warn(
                        `getGrantorGraphqlContent - Serving content from local file as AEM call failed:
                        ${error?.message}`,
                    );
                    return of(modifiedGrantorContent);
                }),
            );
    }
}
